import React from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useHistory, NavLink, useRouteMatch } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import { color } from '../../styles';
import { SUPPORT_URL } from '../../constants';
import { LeftNavListItem } from './leftNavListItem';
import { navSelector } from '../../redux/config/leftnav';
import { showLifeContentSelector } from '../../redux/config/currentNursingHome';
import { showMedicalContentSelector } from '../../redux/config/selfData';
import { useServiceTranslation } from '../../hooks/useServiceTranslation';

interface Props {
  isOpenLeftnav: boolean;
  onClose: () => void;
}

const DrawerContainer = css`
  z-index: 1;
  position: sticky;
  overflow-y: auto;
  top: 0;

  &&.MuiDrawer-root {
    height: 100%;
    width: 240px;
    position: relative;
    z-index: 0;
  }

  .MuiPaper-root {
    position: relative;
    background-color: var(--surface-left-nav);
  }
`;

const DrawerHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 16px;
`;

const SubHeader = css`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px;
  color: ${color.gray};
  span {
    font-weight: bold;
  }
  div {
    padding: 4px 8px;
    border-radius: 4px;
    margin-left: auto;
    color: ${color.dark_gray};
    font-size: 14px;
    &:hover {
      background-color: ${color.bg_gray};
      color: ${color.green};
    }
  }
`;

const SmallHeader = css`
  display: flex;
  align-items: center;
  height: 32px;
  margin-left: 32px;
  color: ${color.dark_gray};
`;

const Logo80 = css`
  width: 80px;
`;

const MedicalLogo = css`
  width: 168px;
  margin-bottom: 4px;
`;

const OpenIcon = css`
  padding: 8px 0 8px 8px;
`;

const ListIcon = css`
  width: 24px;
  height: 24px;
  margin-right: 8px;
`;

const LinkButton = css`
  text-decoration: none;
  color: ${color.gray};
  &:hover {
    color: ${color.green};
  }
`;

const ContactInfo = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: calc(100% - 24px);
  margin: 8px auto 16px auto;
  text-align: left;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  background: ${color.light_gray};
`;

const ContactInfoRow = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 2px;
`;

const ContactInfoValue = css`
  white-space: pre-line;
  overflow-wrap: break-word;
`;

const ContactEmail = css`
  white-space: nowrap;
`;

export const LeftNav = React.memo(({ isOpenLeftnav, onClose }: Props) => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { badge } = useSelector(navSelector);
  const showLifeContent = useSelector(showLifeContentSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { t, i18n, serviceT } = useServiceTranslation();
  const isJa = i18n.language.startsWith('ja');

  const openExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  const handleLogout = React.useCallback(async () => {
    history.push('/logout');
  }, []);

  return (
    <Drawer css={DrawerContainer} anchor={'left'} variant='persistent' open={isOpenLeftnav}>
      <div css={DrawerHeader}>
        <NavLink css={[LinkButton]} to={`${url}/residents`}>
          {!showMedicalContent ? (
            <img css={Logo80} src='/images/login/logo.svg' alt='DFree' />
          ) : (
            <img css={MedicalLogo} src='/images/leftnav/viewer_logo.svg' alt='Medical' />
          )}
        </NavLink>
        <IconButton css={OpenIcon} onClick={onClose}>
          <MenuOpenIcon />
        </IconButton>
      </div>
      <Divider />
      <List>
        <div css={SubHeader}>
          <img css={ListIcon} src='/images/leftnav/icon_user.svg' alt='入居者' />
          <span>{showMedicalContent ? t('resident_medical', '利用者') : t('resident', '入居者')}</span>
          <div>
            <NavLink css={LinkButton} to={`${url}/residents/new`}>
              {t('add', '追加')}
            </NavLink>
          </div>
        </div>
        <LeftNavListItem
          path={`${url}/residents`}
          listItemName={t('dfreeGraph', 'DFreeグラフ')}
          badgeCount={badge.resident}
        />
        {!showMedicalContent && isJa && (
          <LeftNavListItem
            path={`${url}/advice`}
            listItemName={t('advice', 'アドバイス')}
            badgeCount={badge.advice}
            showBadgeNumber={false}
          />
        )}
        <LeftNavListItem
          path={`${url}/records`}
          listItemName={t('excretionTable', '排泄表')}
          badgeCount={badge.careSchedule}
        />
        <LeftNavListItem path={`${url}/bulk-record`} listItemName={t('recordAll', '一括記録')} />
        <LeftNavListItem path={`${url}/notifications`} listItemName={t('notification', '通知')} />
        {!showMedicalContent && isJa && (
          <>
            <div css={SubHeader}>
              <img css={ListIcon} src='/images/leftnav/icon_diaper.svg' alt='消耗品' />
              <span>消耗品</span>
            </div>
            <LeftNavListItem path={`${url}/expendable/order`} listItemName='発注' />
            <LeftNavListItem
              path={`${url}/expendable/saved-order`}
              listItemName='一時保存'
              badgeCount={badge.temporarilySaved}
            />
            <LeftNavListItem path={`${url}/expendable/order-history`} listItemName='発注履歴' />
          </>
        )}
        <div css={SubHeader}>
          <img css={ListIcon} src='/images/leftnav/icon_table.svg' alt='分析' />
          <span>{serviceT('facilitiesUnitAnalysis')}</span>
        </div>
        <div css={SmallHeader}>
          <span>{t('csvdownload', 'CSVダウンロード')}</span>
        </div>
        <LeftNavListItem path={`${url}/analysis/ultrasonic-data`} listItemName={t('measurementData', '測定データ')} />
        <LeftNavListItem
          path={`${url}/analysis/care-records`}
          listItemName={showMedicalContent ? t('urinationDiary', '排尿日誌') : t('careHistory', 'ケア履歴')}
        />
        {showLifeContent && <LeftNavListItem path={`${url}/analysis/life`} listItemName={'LIFE'} />}
        {isJa && (
          <>
            <div css={SmallHeader}>
              <span>消耗品利用状況</span>
            </div>
            <LeftNavListItem path={`${url}/analysis/equipments`} listItemName='製品種類・サイズ別' />
          </>
        )}
        <div css={SubHeader}>
          <img css={ListIcon} src='/images/leftnav/icon_cog.svg' alt='一覧・登録' />
          <span>{t('listRegister', '一覧・登録')}</span>
        </div>
        <LeftNavListItem path={`${url}/settings/unit`} listItemName={t(isJa ? 'unit' : 'unitList')} />
        <LeftNavListItem path={`${url}/expendable`} listItemName={serviceT('laxativesAndDrinks')} />
        {isJa && !showMedicalContent && (
          <>
            <div css={SubHeader}>
              <MenuBookTwoToneIcon css={ListIcon} aria-label='操作手順書' />
              <span>操作手順書</span>
            </div>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() => openExternalLink(SUPPORT_URL.U2PManual.ja)}
            >
              <span>{'DFree (新型)'}</span>
            </ListItemButton>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() => openExternalLink(SUPPORT_URL.U2Manual.ja)}
            >
              <span>{'DFree'}</span>
            </ListItemButton>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() => openExternalLink(SUPPORT_URL.BenManual.ja)}
            >
              <span>{'DFree Ben'}</span>
            </ListItemButton>
          </>
        )}
        {isJa && !showMedicalContent && (
          <>
            <div css={SubHeader}>
              <DescriptionOutlinedIcon css={ListIcon} aria-label='分析・活用' />
              <span>{'分析・活用'}</span>
            </div>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() => openExternalLink(SUPPORT_URL.cases.ja)}
            >
              <span>{'DFree施設事例'}</span>
            </ListItemButton>
          </>
        )}
        <div css={SubHeader}>
          <img css={ListIcon} src='/images/leftnav/icon_help.svg' alt='困ったときは' />
          <span>{t('whenYouNeedHelp', '困ったときは')}</span>
        </div>
        {showMedicalContent ? (
          <>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() =>
                openExternalLink(isJa ? SUPPORT_URL.medBasicUsage.ja : SUPPORT_URL.medBasicUsage.en)
              }
            >
              <span>{t('basicUsageVideo', '基本の使い方（動画）')}</span>
            </ListItemButton>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() =>
                openExternalLink(isJa ? SUPPORT_URL.instructionManual.ja : SUPPORT_URL.instructionManual.en)
              }
            >
              <span>{t('instructionManual', 'DFree Viewer取扱説明書')}</span>
            </ListItemButton>
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() =>
                openExternalLink(isJa ? SUPPORT_URL.medTroubleshooting.ja : SUPPORT_URL.medTroubleshooting.en)
              }
            >
              <span>{t('troubleshooting', '困った時の対処法')}</span>
            </ListItemButton>
          </>
        ) : (
          <>
            {isJa && (
              <ListItemButton
                sx={{ padding: '8px 16px 8px 40px' }}
                onClick={() => openExternalLink(SUPPORT_URL.basics.ja)}
              >
                <span>{'基本の使い方'}</span>
              </ListItemButton>
            )}
            <ListItemButton
              sx={{ padding: '8px 16px 8px 40px' }}
              onClick={() => openExternalLink(isJa ? SUPPORT_URL.howToWear.ja : SUPPORT_URL.howToWear.en)}
            >
              <span>{t('howToAttachSensor', '装着手順')}</span>
            </ListItemButton>
            {isJa && (
              <>
                <ListItemButton
                  sx={{ padding: '8px 16px 8px 40px' }}
                  onClick={() => openExternalLink(SUPPORT_URL.troubleshooting.ja)}
                >
                  <span>{'困った時の対処法'}</span>
                </ListItemButton>
                <ListItemButton
                  sx={{ padding: '8px 16px 8px 40px' }}
                  onClick={() => openExternalLink(SUPPORT_URL.faq.ja)}
                >
                  <span>{'よくあるご質問'}</span>
                </ListItemButton>
              </>
            )}
          </>
        )}
        <div css={SubHeader}>
          <ComputerOutlinedIcon css={ListIcon} aria-label='お問い合わせ' />
          <span>{t('contactUs', 'お問い合わせ')}</span>
        </div>
        <LeftNavListItem
          path={`${url}/chat`}
          listItemName={t('chatRoom', 'チャットルーム')}
          badgeCount={badge.chatUnreadCount}
          showBadgeNumber={false}
        />
        {isJa && !showMedicalContent && (
          <ListItemButton
            sx={{ padding: '8px 16px 8px 40px' }}
            onClick={() => openExternalLink(SUPPORT_URL.contact.ja)}
          >
            <span>{t('inquiryForm', 'お問い合わせフォーム')}</span>
          </ListItemButton>
        )}
        <div css={ContactInfo}>
          <div css={ContactInfoRow}>
            <span>{`${t('phone', '電話')}:`}</span>
            <span css={ContactInfoValue}>{t('contactPhoneNumber')}</span>
          </div>
          <div css={ContactInfoRow}>
            <span>{`${t('email', 'メール')}:`}</span>
            <span css={ContactEmail}>
              {showMedicalContent && isJa ? 'medical-care@dfree.co.jp' : t('contactEmail')}
            </span>
          </div>
        </div>
        <Divider />
        <div css={SubHeader} onClick={handleLogout}>
          <img css={ListIcon} src='/images/icons/logout.svg' alt='ログアウト' />
          <span>{t('logout', 'ログアウト')}</span>
        </div>
      </List>
    </Drawer>
  );
});
